import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkle, Check } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetupGuidePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gameName = searchParams.get('game') || 'Catan';
  const [steps, setSteps] = useState([]);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuide();
  }, [gameName]);

  const fetchGuide = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ai/setup-guide`, { params: { game_name: gameName }, withCredentials: true });
      setSteps(Array.isArray(data.steps) ? data.steps : []);
    } catch (err) { toast.error('Failed to load setup guide'); }
    finally { setLoading(false); }
  };

  const toggleStep = (stepNum) => {
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.has(stepNum) ? next.delete(stepNum) : next.add(stepNum);
      return next;
    });
  };

  const progress = steps.length > 0 ? Math.round((completedSteps.size / steps.length) * 100) : 0;

  return (
    <div className="app-container" data-testid="setup-guide-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Sparkle size={24} weight="duotone" className="text-[#00F0FF]" />
              Setup: {gameName}
            </h1>
            <p className="text-[#71717A] text-xs">Step-by-step setup guide</p>
          </div>
        </div>

        <div className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 mb-3">
          <div className="flex justify-between mb-1">
            <span className="text-[#A1A1AA] text-xs">{completedSteps.size}/{steps.length} steps done</span>
            <span className="text-white text-sm font-bold">{progress}%</span>
          </div>
          <div className="h-2 bg-[#181824] rounded-full overflow-hidden">
            <motion.div animate={{ width: `${progress}%` }} className="h-full bg-gradient-to-r from-[#00F0FF] to-[#9D4CDD] rounded-full" />
          </div>
        </div>
      </header>

      <div className="px-6 pb-32 space-y-2">
        {loading ? (
          [1,2,3,4].map(i => <div key={i} className="h-20 bg-[#181824] rounded-xl animate-pulse" />)
        ) : (
          steps.map((s, i) => {
            const done = completedSteps.has(s.step);
            return (
              <motion.button key={s.step} onClick={() => toggleStep(s.step)}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className={`w-full p-4 rounded-xl border text-left flex gap-3 transition-all ${done ? 'bg-[#00F0FF]/5 border-[#00F0FF]/20' : 'bg-[#0F0F16] border-white/5 hover:border-[#9D4CDD]/30'}`}
                data-testid={`setup-step-${s.step}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${done ? 'bg-[#00F0FF] text-[#0A0A12]' : 'bg-[#181824] text-[#71717A]'}`}>
                  {done ? <Check size={16} weight="bold" /> : s.step}
                </div>
                <div>
                  <h3 className={`text-sm font-medium ${done ? 'text-[#00F0FF]' : 'text-white'}`}>{s.title}</h3>
                  <p className="text-[#71717A] text-xs mt-0.5">{s.description}</p>
                </div>
              </motion.button>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
}
